/**
 * The game layer.
 *
 * Everything above the renderer: orders, targets, actors, combat and the HUD.
 * The renderer knows nothing about any of it — it reads controllers, and this
 * writes intent onto them.
 *
 * Frame contract, from `main.js`:
 *   preUpdate(dt)    before the player controller runs: consume the click, tick
 *                    combat and AI, write every intent.
 *   updateActors(dt) after the player's figure and contact pass: advance mob
 *                    controllers, figures and contacts. Must stay ahead of
 *                    `terrain.update()`, which consumes the deform brushes.
 *   sync(cameraPos)  alongside the player figure's own uniform push.
 *   postRender(dtMs) HUD refresh.
 */

import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { input, takeClick, takeHotbar } from "../core/input.js";
import { angleDelta } from "../character/controller.js";
import { screenRay, groundPick, actorPick } from "./targeting.js";
import { seek, halt, distanceTo, angleTo } from "./nav.js";
import { gameState } from "./gameState.js";
import { ActorSystem } from "./actors.js";
import { Combat } from "./combat.js";
import { Hud } from "../ui/hud/hud.js";
import { SKILLS, ITEMS, QUESTS, TELEPORTS } from "./worldData.js";
import { audio } from "./audio.js";
import { S } from "../core/settings.js";

/** How far the surf steer follows the camera. Was the mouse-yaw term. */
const SURF_FOLLOW = 1.25;
/** How close you must stand to an NPC to talk. */
const TALK_RANGE = 4.5;
/** Potion strengths, straight from the original. */
const HP_POTION = 150;
const MP_POTION = 100;
/** How far away a name plate is still worth drawing. */
const PLATE_RANGE = 55;

const _screenPoint = { x: 0, y: 0 };

export class Game {
    /**
     * @param {{
     *   canvas: HTMLCanvasElement,
     *   scene: import("@babylonjs/core/scene").Scene,
     *   rig: import("../core/camera.js").CameraRig,
     *   terrain: import("../terrain/terrain.js").Terrain,
     *   sky: import("../render/sky.js").Sky,
     *   shadows: import("../render/shadows.js").ShadowSystem,
     *   spray: import("../vfx/particles.js").SprayField,
     *   depthPass: import("../render/depthPass.js").DepthPass,
     *   player: import("../character/controller.js").CharacterController,
     *   spells: import("../spells/spellSystem.js").SpellSystem,
     * }} ctx
     */
    constructor(ctx) {
        this.ctx = ctx;
        this.canvas = ctx.canvas;
        this.scene = ctx.scene;
        this.rig = ctx.rig;
        this.terrain = ctx.terrain;
        this.player = ctx.player;
        this.spells = ctx.spells;

        this.state = gameState;

        /** @type {{x:number, z:number}|null} Standing move order. */
        this.destination = null;
        /** Something to do once the walk finishes. */
        this.pendingInteract = null;
        /** Snow surf held from the hotbar rather than the R key. */
        this.surfLocked = false;
        /** @type {object|null} NPC whose dialog is open. */
        this.activeNpc = null;

        this.actors = new ActorSystem({
            scene: ctx.scene,
            terrain: ctx.terrain,
            sky: ctx.sky,
            shadows: ctx.shadows,
            spray: ctx.spray,
            depthPass: ctx.depthPass,
            player: ctx.player,
        });

        this.combat = new Combat(this);
        this.hud = new Hud(this);

        this.player.onTrauma = (a) => this.rig.addTrauma(a);
    }

    /** Compile the actor pipelines behind the loading screen. */
    warmUp() {
        return this.actors.warmUp();
    }

    // ======================================================= orders & clicks
    stopMoving() {
        this.destination = null;
        this.pendingInteract = null;
        halt(this.player);
    }

    moveTo(x, z) {
        // A move order is what breaks a cast — not the character's leftover
        // momentum, which would cancel every spell cast at the end of a walk.
        this.combat?.cancelCast("Cast interrupted.");
        this.destination = { x, z };
        if (this.state.isSitting) this.stand();
    }

    teleport(x, z) {
        this.stopMoving();
        this.combat.disengage();
        this.player.position.set(x, this.terrain.heightAt(x, z), z);
        this.player.velocity.set(0, 0, 0);
        // The planted feet and the camera spring are both world-space state that
        // a teleport invalidates: without this the legs reach for the old
        // continent and the arm sweeps the whole distance.
        this.ctx.figure?.rebind(this.player);
        this.rig.snap();
    }

    /**
     * Everything that has to happen before the player controller integrates.
     * @param {number} dt
     */
    preUpdate(dt) {
        this._consumeClick();
        this._consumeHotbar();
        this.combat.tick(dt);
        this._writePlayerIntent(dt);
        this._checkPendingInteract();
    }

    _consumeClick() {
        const click = takeClick();
        if (!click || this.state.isDead) return;

        const { origin, dir } = screenRay(this.rig, this.canvas, click.x, click.y);

        const hit = actorPick(this.actors.pickables(), origin, dir);
        if (hit) {
            this._clickActor(hit.actor, click.double);
            return;
        }

        const ground = groundPick(this.terrain, origin, dir);
        if (ground) {
            this.combat.disengage();
            this.pendingInteract = null;
            this.moveTo(ground.x, ground.z);
        }
    }

    /**
     * Lineage's two-stage click: the first one selects, the second one acts.
     * A double click does both at once, which is what everyone does anyway.
     */
    _clickActor(actor, double) {
        const already = this.combat.target === actor;
        if (!already) this.combat.setTarget(actor);

        if (!already && !double) return;

        if (actor.kind === "npc") {
            this.pendingInteract = { kind: "talk", actor };
            this.moveTo(actor.position.x, actor.position.z);
        } else if (!actor.isDead) {
            this.combat.engage();
        }
    }

    _consumeHotbar() {
        const slot = takeHotbar();
        if (slot) this.useHotbar(slot - 1);
        // The raw spell keys stay live for the console and for looking at the
        // effects on their own; `spellSystem` reads them itself.
    }

    _checkPendingInteract() {
        const p = this.pendingInteract;
        if (!p) return;
        const dist = distanceTo(this.player, p.actor.position.x, p.actor.position.z);
        if (dist > TALK_RANGE) return;

        this.pendingInteract = null;
        this.stopMoving();
        this.player.intent.faceAngle =
            angleTo(this.player, p.actor.position.x, p.actor.position.z);
        if (p.kind === "talk") this.openNpcDialog(p.actor.def);
    }

    _writePlayerIntent(dt) {
        const p = this.player;
        const it = p.intent;
        const st = this.state;

        it.surf = (input.surf || this.surfLocked) && !st.isDead && !st.isSitting;
        it.brake = false;
        it.steer = it.surf
            ? Math.max(-1, Math.min(1, angleDelta(p.facing, this.rig.yaw) * SURF_FOLLOW))
            : 0;

        if (it.surf) {
            this.destination = null;
            return;
        }

        if (st.isDead || st.isSitting || st.isRooted || this.combat.cast) {
            halt(p);
            this.destination = null;
            return;
        }

        // The combat chase writes its own intent; only walk if it did not.
        if (this.combat.autoAttack && this.combat.target && !this.combat.target.isDead) {
            return;
        }
        it.faceAngle = null;

        if (this.destination) {
            const arrived = seek(
                p, this.destination.x, this.destination.z, st.stats.moveSpeed
            );
            if (arrived) this.destination = null;
        } else {
            halt(p);
        }
        void dt;
    }

    /**
     * Mob and NPC simulation. Runs after the player's figure so the player is
     * always the first thing solved in a frame, and before `terrain.update()`
     * so actor footprints make it into this frame's brushes.
     * @param {number} dt
     */
    updateActors(dt) {
        this.actors.update(dt);
    }

    /** @param {Vector3} cameraPos */
    sync(cameraPos) {
        this.actors.sync(cameraPos);
    }

    /** @param {number} dtMs */
    postRender(dtMs) {
        this.hud.update(dtMs);
    }

    // ============================================================== actions
    /** @param {number} index 0..11 */
    useHotbar(index) {
        const slot = this.state.hotbar[index];
        if (!slot) return;

        if (slot.type === "skill") {
            this.combat.castSkill(slot.targetId);
        } else if (slot.type === "item") {
            this.usePotion(slot.targetId);
        } else if (slot.type === "action") {
            if (slot.targetId === "sit") this.toggleSit();
            else if (slot.targetId === "surf") this.toggleSurf();
        }
    }

    usePotion(itemId) {
        const st = this.state;
        if (st.isDead) return;
        if (!st.consumeItem(itemId, 1)) {
            st.system("None left.");
            return;
        }
        const p = this.player.position;
        if (itemId === "hp_potion") {
            const healed = Math.min(st.stats.maxHp - st.stats.hp, HP_POTION);
            st.stats.hp += healed;
            st.addFloatingText(`+${healed} HP`, p.x, p.y + 2.1, p.z, "#22c55e");
        } else if (itemId === "mp_potion") {
            const gained = Math.min(st.stats.maxMp - st.stats.mp, MP_POTION);
            st.stats.mp += gained;
            st.addFloatingText(`+${gained} MP`, p.x, p.y + 2.1, p.z, "#38bdf8");
        }
        st.notify();
    }

    /** Clicking a bag slot: gear equips, potions are drunk, shots toggle. */
    useInventoryItem(stack) {
        const st = this.state;
        const item = stack.item;
        if (item.type === "consumable") {
            if (item.id === "soulshot_ng" || item.id === "spiritshot_ng") st.toggleSoulshot();
            else this.usePotion(item.id);
            return;
        }
        if (stack.isEquipped) st.unequipItem(item.slot);
        else st.equipItem(stack);
    }

    toggleSit() {
        const st = this.state;
        if (st.isDead) return;
        st.isSitting = !st.isSitting;
        if (st.isSitting) this.stopMoving();
        st.system(st.isSitting ? "You sit down to rest." : "You stand up.");
    }

    stand() {
        if (this.state.isSitting) {
            this.state.isSitting = false;
            this.state.notify();
        }
    }

    toggleSurf() {
        this.surfLocked = !this.surfLocked;
        this.state.system(this.surfLocked ? "Board out." : "Board away.");
    }

    revive() {
        this.combat.revive();
    }

    buyItem(itemId, quantity, cost) {
        const st = this.state;
        if (st.stats.adena < cost) {
            st.system("Not enough adena.");
            return;
        }
        st.stats.adena -= cost;
        st.addItem(itemId, quantity);
        st.system(`Bought ${ITEMS[itemId].name} ×${quantity}.`);
        st.saveState();
        st.notify();
    }

    turnInQuest(questId) {
        this.state.turnInQuest(questId);
    }

    // ============================================================== NPC flow
    openNpcDialog(npcDef) {
        this.activeNpc = npcDef;
        this.hud.openModal("npc");
    }

    /**
     * @param {object} npc
     * @param {{action:string, targetId?:string, label:string}} option
     */
    npcAction(npc, option) {
        const st = this.state;
        switch (option.action) {
            case "teleport": {
                const dest = TELEPORTS[option.targetId];
                if (!dest) return;
                this.hud.closeModal();
                this.teleport(dest.x, dest.z);
                st.system(`Teleported to ${option.targetId.replace(/_/g, " ")}.`);
                st.notify();
                break;
            }
            case "heal": {
                st.stats.hp = st.stats.maxHp;
                st.stats.mp = st.stats.maxMp;
                st.stats.cp = st.stats.maxCp;
                const p = this.player.position;
                st.addFloatingText("FULL HEAL!", p.x, p.y + 2.4, p.z, "#22c55e", true);
                audio.playMagicCast(true);
                st.system("The Drift's blessing restores you.");
                this.hud.closeModal();
                break;
            }
            case "class_transfer":
                this.hud.openModal("classes");
                break;
            case "shop":
                this.hud.openModal("shop");
                break;
            case "quest": {
                const progress = st.quests[option.targetId];
                if (!progress) st.acceptQuest(option.targetId);
                else if (progress.isCompleted && !progress.isTurnedIn) {
                    st.turnInQuest(option.targetId);
                }
                this.hud.renderModal();
                break;
            }
            default:
                st.addChat(npc.name, "all", "Soulshots double your physical damage. Keep them loaded.");
                break;
        }
    }

    onClassChanged() {
        this.hud.closeModal();
    }

    // ============================================================ HUD feeds
    get playerPos() {
        return this.player.position;
    }

    targetInfo() {
        const t = this.combat.target;
        if (!t) return null;
        if (t.kind === "npc") {
            return { kind: "npc", name: t.name, title: t.title };
        }
        return {
            kind: "mob",
            name: t.name,
            level: t.level,
            type: t.type,
            hp: Math.max(0, t.hp),
            maxHp: t.maxHp,
        };
    }

    /** Minimap dots, player-relative. */
    blips() {
        const list = [];
        const selected = this.combat.target;
        for (const mob of this.actors.mobs) {
            if (mob.isDead) continue;
            list.push({
                x: mob.position.x,
                z: mob.position.z,
                kind: mob.type === "boss" ? "boss" : "mob",
                selected: mob === selected,
            });
        }
        for (const npc of this.actors.npcs) {
            list.push({
                x: npc.position.x,
                z: npc.position.z,
                kind: "npc",
                selected: npc === selected,
            });
        }
        return list;
    }

    /** Name plates for everything close enough to read. */
    plates() {
        if (S.showPlates === false) return [];
        const out = [];
        const p = this.player.position;
        const selected = this.combat.target;

        for (const mob of this.actors.mobs) {
            if (mob.isDead) continue;
            if (Math.hypot(mob.position.x - p.x, mob.position.z - p.z) > PLATE_RANGE) continue;
            out.push({
                id: mob.id,
                name: mob.name,
                sub: `Lv ${mob.level}`,
                kind: mob.type === "boss" ? "boss" : mob.type,
                selected: mob === selected,
                hpPct: mob.hp / mob.maxHp,
                x: mob.position.x,
                y: mob.headY,
                z: mob.position.z,
            });
        }
        for (const npc of this.actors.npcs) {
            if (Math.hypot(npc.position.x - p.x, npc.position.z - p.z) > PLATE_RANGE) continue;
            out.push({
                id: npc.id,
                name: npc.name,
                sub: npc.title,
                kind: "npc",
                selected: npc === selected,
                hpPct: null,
                x: npc.position.x,
                y: npc.headY,
                z: npc.position.z,
            });
        }
        return out;
    }

    castProgress() {
        return this.combat.castProgress();
    }

    /** Triangles the actors are actually drawing, for the overlay's tally. */
    get drawnTriangles() {
        let n = 0;
        for (const npc of this.actors.npcs) if (npc.drawn) n += npc.render.triangles;
        for (const body of this.actors.pool) if (body._owner) n += body.triangles;
        return n;
    }

    skillCooldown(skillId) {
        return this.combat.cooldownLeft(skillId);
    }

    /**
     * World point to CSS pixels, or null when it is behind the camera.
     *
     * Done against the rig's own basis rather than through Babylon's projection
     * because the post chain freezes a TAA-jittered matrix onto the camera every
     * frame, and because the render target may be scaled away from the CSS size.
     */
    project(x, y, z) {
        const cam = this.rig.camera.position;
        const dx = x - cam.x;
        const dy = y - cam.y;
        const dz = z - cam.z;

        const f = this.rig.forward;
        const depth = dx * f.x + dy * f.y + dz * f.z;
        if (depth < 0.2) return null;

        const r = this.rig.right;
        const ux = -this.rig.up.x;
        const uy = -this.rig.up.y;
        const uz = -this.rig.up.z;

        const right = dx * r.x + dy * r.y + dz * r.z;
        const up = dx * ux + dy * uy + dz * uz;

        const w = this.canvas.clientWidth;
        const h = this.canvas.clientHeight;
        const tanHalf = Math.tan(this.rig.fov * 0.5);
        const aspect = w / Math.max(1, h);

        const ndcX = right / (depth * tanHalf * aspect);
        const ndcY = up / (depth * tanHalf);
        if (ndcX < -1.6 || ndcX > 1.6 || ndcY < -1.6 || ndcY > 1.6) return null;

        _screenPoint.x = (ndcX * 0.5 + 0.5) * w;
        _screenPoint.y = (0.5 - ndcY * 0.5) * h;
        return _screenPoint;
    }

    // ============================================================== bindings
    /** Escape: close what is open, else drop the target, else stop. */
    escape() {
        if (this.hud.modalOpen) {
            this.hud.closeAll();
            this.activeNpc = null;
            return;
        }
        if (this.combat.target) {
            this.combat.clearTarget();
            return;
        }
        this.stopMoving();
    }

    /** @param {string} name */
    toggleModal(name) {
        this.hud.toggleModal(name);
    }

    focusChat() {
        this.hud.focusChat();
    }
}

export { SKILLS, QUESTS };
